import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Descriptions,
  Button as AntButton,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  DollarOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  WorkExecutionData,
  ExecutionLine,
  WorkExecutionStatus,
  ExecutionBatch,
} from "./types";
import { mockWorkExecutionData } from "./mockData/workExecutionData";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";

const { Panel } = AntCollapse;

const WorkExecutionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [workExecution, setWorkExecution] = useState<WorkExecutionData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
    onHold: 1,
    resume: 1,
    complete: 1,
    close: 1,
  });

  useEffect(() => {
    const updateScreenSize = () => {
      setScreenSize(window.innerWidth);
      setMobileView(window.innerWidth < 768);
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);

    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  useEffect(() => {
    if (id) {
      loadWorkExecutionData(id);
    }
  }, [id]);

  const updateSm = () => {
    if (screenSize > 990) {
      setSm(!sm);
    } else {
      setSm(false);
    }
  };

  const loadWorkExecutionData = async (executionId: string) => {
    try {
      setLoading(true);

      // Find work execution data by ID or workOrderId
      const data = mockWorkExecutionData.find(
        (exec) => exec.id === executionId || exec.workOrderId === executionId
      );

      if (data) {
        setWorkExecution(data);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Work Execution not found",
        });
      }
    } catch (error) {
      console.error("Error loading work execution:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load work execution data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (workExecution) {
      const searchParams = new URLSearchParams(location.search);
      const flowType = searchParams.get("flowType");
      const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
      navigate(
        `/work-order-creation/?edit=${workExecution.workOrderId}${flowTypeQuery}`
      );
    }
  };

  const handleOnHold = () => {
    if (workExecution) {
      Swal.fire({
        title: "Put Work Execution on Hold?",
        text: "Please provide a reason for putting this work execution on hold.",
        input: "textarea",
        inputPlaceholder: "Enter reason for hold...",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Put on Hold",
        confirmButtonColor: "#ffc107",
      }).then((result) => {
        if (result.isConfirmed) {
          const updatedExecution = {
            ...workExecution,
            status: WorkExecutionStatus.OnHold,
          };
          setWorkExecution(updatedExecution);
          Swal.fire(
            "Success!",
            "Work execution has been put on hold.",
            "success"
          );
        }
      });
    }
  };

  const handleResume = () => {
    if (workExecution) {
      const updatedExecution = {
        ...workExecution,
        status: WorkExecutionStatus.InProgress,
      };
      setWorkExecution(updatedExecution);
      Swal.fire("Success!", "Work execution has been resumed.", "success");
    }
  };

  const handleComplete = () => {
    if (workExecution) {
      Swal.fire({
        title: "Complete Work Execution?",
        text: "This action will mark the work execution as completed.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Complete",
        confirmButtonColor: "#198754",
      }).then((result) => {
        if (result.isConfirmed) {
          const updatedExecution = {
            ...workExecution,
            status: WorkExecutionStatus.Completed,
            actualEndDate: new Date().toISOString(),
            progressPercentage: 100,
          };
          setWorkExecution(updatedExecution);
          Swal.fire(
            "Success!",
            "Work execution has been completed.",
            "success"
          );
        }
      });
    }
  };

  const getStatusColor = (
    status: WorkExecutionStatus
  ): { color: string; bgColor: string } => {
    switch (status) {
      case WorkExecutionStatus.NotStarted:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
      case WorkExecutionStatus.InProgress:
        return { color: "#007BFF", bgColor: "#E3F2FD" };
      case WorkExecutionStatus.OnHold:
        return { color: "#FFC107", bgColor: "#FFFDE7" };
      case WorkExecutionStatus.Completed:
        return { color: "#198754", bgColor: "#D4EDDA" };
      case WorkExecutionStatus.Closed:
        return { color: "#495057", bgColor: "#E9ECEF" };
      case WorkExecutionStatus.Cancelled:
        return { color: "#DC3545", bgColor: "#F8D7DA" };
      default:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
    }
  };
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);

  const handleApprove = async () => {
    try {
      setDisableApprove(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock approve functionality
      // const updatedWorkOrder: any = { ...workOrder!, status: "Approved" };
      // setWorkOrder(updatedWorkOrder);

      Swal.fire(
        "Approved!",
        "Work Execution has been approved successfully",
        "success"
      ).then(() => {
        navigate(-1);
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve Work Execution", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock reject functionality
      // const updatedWorkOrder: any = {
      //   ...workOrder!,
      //   status: "Rejected",
      //   rejectionComments: rejectedReason,
      // };
      // setWorkOrder(updatedWorkOrder);

      Swal.fire(
        "Rejected!",
        "Work Execution has been rejected",
        "success"
      ).then(() => {
        navigate(-1);
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject Work Execution", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work Order",
      html: `
          <textarea 
            id="rejection-reason" 
            placeholder="Enter rejection reason (required)"
            style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
          ></textarea>
        `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleReject(result.value);
      }
    });
  };

  // Execution Lines Table Columns
  const executionLinesColumns: TableColumnsType<ExecutionLine> = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Line #",
      dataIndex: "lineNumber",
      key: "lineNumber",
      width: 80,
      fixed: "left",
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      render: (text: string, record: ExecutionLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Approved Quantity",
      dataIndex: "approvedQuantity",
      key: "approvedQuantity",
      width: 120,
      render: (quantity: number, record: ExecutionLine) =>
        `${formatDecimal(quantity)} ${record?.uom}`,
    },
    {
      title: "Executed Quantity",
      dataIndex: "executedQuantity",
      key: "executedQuantity",
      width: 120,
      render: (quantity: number, record: ExecutionLine) => (
        <span className="text-success fw-bold">
          {`${formatDecimal(quantity)} ${record?.uom}`}
        </span>
      ),
    },
    {
      title: "Cumulative Executed",
      dataIndex: "cumulativeExecuted",
      key: "cumulativeExecuted",
      width: 130,
      render: (quantity: number, record: ExecutionLine) =>
        `${formatDecimal(quantity)} ${record?.uom}`,
    },
    {
      title: "% Work Completion",
      dataIndex: "workCompletionPercentage",
      key: "workCompletionPercentage",
      width: 130,
      render: (percentage: number) => (
        <div style={{ textAlign: "center" }}>
          <span
            className={`badge ${
              percentage === 100
                ? "bg-success"
                : percentage >= 75
                ? "bg-info"
                : percentage >= 50
                ? "bg-warning"
                : "bg-secondary"
            }`}
          >
            {percentage}%
          </span>
        </div>
      ),
    },
    {
      title: "Remaining Quantity",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      width: 130,
      render: (quantity: number, record: ExecutionLine) => (
        <span className="text-warning">
          {`${formatDecimal(quantity)} ${record?.uom}`}
        </span>
      ),
    },
    {
      title: "Rate (₹)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (rate: number) => `₹${rate?.toLocaleString()}`,
    },
    {
      title: "Executed Value",
      dataIndex: "executedValue",
      key: "executedValue",
      width: 120,
      render: (value: number) => (
        <span className="text-success fw-bold">₹{value?.toLocaleString()}</span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      render: (remarks: string) => remarks || "---",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const statusColors: { [key: string]: string } = {
          Pending: "orange",
          "In Progress": "blue",
          Completed: "green",
          "On Hold": "red",
        };
        return (
          <Tag color={statusColors[status] || "default"}>
            {status || "Pending"}
          </Tag>
        );
      },
    },
  ];

  if (loading) {
    return (
      <>
        <Head title="Work Execution Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workExecution) {
    return (
      <>
        <Head title="Work Execution Not Found" />
        <Content>
          <Block size="lg">
            <Alert color="danger">Work Execution not found</Alert>
          </Block>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusColor(workExecution.status);

  // Calculate totals
  const totalApprovedValue =
    workExecution.executionLines?.reduce(
      (sum, line) => sum + line.approvedQuantity * line.rate,
      0
    ) || 0;

  const totalExecutedValue =
    workExecution.executionLines?.reduce(
      (sum, line) => sum + line.executedValue,
      0
    ) || 0;

  const totalRemainingValue =
    workExecution.executionLines?.reduce(
      (sum, line) => sum + line.remainingQuantity * line.rate,
      0
    ) || 0;

  const avgProgress =
    workExecution.executionLines && workExecution.executionLines.length > 0
      ? Math.round(
          workExecution.executionLines.reduce(
            (sum, line) => sum + line.workCompletionPercentage,
            0
          ) / workExecution.executionLines.length
        )
      : 0;

  return (
    <>
      <Head title="Work Execution Details" />
      <Content>
        <Block size="lg">
          <div
            className={`card-aside-wrap ${sm ? "card-aside-toggle" : ""}`}
            id="user-aside"
          >
            <div className="card-inner card-inner-lg">
              {mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workExecution.workOrderNumber} | {workExecution.status}
                      </span>
                      <Badge color="info" className="ml-2">
                        Work Execution
                      </Badge>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Execution specific actions */}
                    {/* {workExecution.status === WorkExecutionStatus.InProgress && allowedActions?.onHold === 1 && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="warning" onClick={handleOnHold}>
                              <Icon name="pause" /> Put on Hold
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    {/* {workExecution.status === WorkExecutionStatus.OnHold && allowedActions?.resume === 1 && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="success" onClick={handleResume}>
                              <Icon name="play" /> Resume
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    {/* {(workExecution.status === WorkExecutionStatus.InProgress || 
                      workExecution.status === WorkExecutionStatus.OnHold) && 
                      allowedActions?.complete === 1 && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="success" onClick={handleComplete}>
                              <Icon name="check" /> Complete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    {/* {workExecution.status === "In Progress" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="primary" onClick={handleEdit}>
                              <EditOutlined /> Edit Work Order
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    {workExecution.status == "Pending Approvals" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            {disableApprove ? (
                              <Button color="success" type="button">
                                <Spinner size="sm" color="light" />
                              </Button>
                            ) : (
                              <AntButton
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => {
                                  Swal.fire({
                                    title: "Are you sure?",
                                    text: "Do you want to approve this Work Order?",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonText: "Yes, Approve it!",
                                    cancelButtonText: "No, keep it",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      handleApprove();
                                    }
                                  });
                                }}
                              >
                                Approve
                              </AntButton>
                            )}
                          </li>
                          <li className="pt-0 pb-0">
                            {disableReject ? (
                              <Button color="danger" type="button">
                                <Spinner size="sm" color="light" />
                              </Button>
                            ) : (
                              <AntButton
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={showRejectModal}
                              >
                                Reject
                              </AntButton>
                            )}
                          </li>
                        </ul>
                      </div>
                    )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        {/* <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li> */}

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => navigate(-1)}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <AntCollapse
                  defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]}
                >
                  {/* Section A: Work Execution Overview */}
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>📊 Work Execution Overview</>
                      </div>
                    }
                    key="1"
                  >
                    <Row>
                      <Col lg="6">
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="Work Order Number">
                            {workExecution.workOrderNumber}
                          </Descriptions.Item>
                          <Descriptions.Item label="Work Order Title">
                            {workExecution.workOrderTitle}
                          </Descriptions.Item>
                          <Descriptions.Item label="Site">
                            {workExecution.siteName}
                          </Descriptions.Item>
                          <Descriptions.Item label="Project">
                            {workExecution.projectName || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Contractor">
                            {workExecution.contractorName}
                          </Descriptions.Item>
                          <Descriptions.Item label="Contract Type">
                            {workExecution.contractType}
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                      <Col lg="6">
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="Status">
                            <span
                              className="badge"
                              style={{
                                color: statusInfo.color,
                                backgroundColor: statusInfo.bgColor,
                                border: `1px solid ${statusInfo.color}`,
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                            >
                              {workExecution.status}
                            </span>
                          </Descriptions.Item>
                          <Descriptions.Item label="Progress">
                            <div
                              className="progress"
                              style={{ height: "20px" }}
                            >
                              <div
                                className={`progress-bar ${
                                  workExecution.progressPercentage === 100
                                    ? "bg-success"
                                    : workExecution.progressPercentage >= 75
                                    ? "bg-info"
                                    : workExecution.progressPercentage >= 50
                                    ? "bg-warning"
                                    : "bg-secondary"
                                }`}
                                style={{
                                  width: `${workExecution.progressPercentage}%`,
                                }}
                              >
                                {workExecution.progressPercentage}%
                              </div>
                            </div>
                          </Descriptions.Item>
                          <Descriptions.Item label="Planned Duration">
                            {dayjs(workExecution.plannedStartDate).format(
                              "DD/MM/YYYY"
                            )}{" "}
                            -{" "}
                            {dayjs(workExecution.plannedEndDate).format(
                              "DD/MM/YYYY"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label="Actual Duration">
                            {workExecution.actualStartDate
                              ? dayjs(workExecution.actualStartDate).format(
                                  "DD/MM/YYYY"
                                )
                              : "Not Started"}{" "}
                            -{" "}
                            {workExecution.actualEndDate
                              ? dayjs(workExecution.actualEndDate).format(
                                  "DD/MM/YYYY"
                                )
                              : "Ongoing"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Total Value">
                            <span className="fw-bold">
                              ₹{workExecution.totalValue?.toLocaleString()}
                            </span>
                          </Descriptions.Item>
                          <Descriptions.Item label="Executed Value">
                            <span className="text-success fw-bold">
                              ₹{workExecution.executedValue?.toLocaleString()}
                            </span>
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                    </Row>
                  </Panel>

                  {/* Section B: Execution Lines */}
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>
                          📋 Work Execution Lines (
                          {workExecution.executionLines?.length || 0})
                        </>
                      </div>
                    }
                    key="2"
                  >
                    <div className="mb-3">
                      <Tag color="purple">
                        {workExecution.executionLines?.length || 0} execution
                        line(s)
                      </Tag>
                      <Tag color="green" className="ms-2">
                        Avg Progress: {avgProgress}%
                      </Tag>
                    </div>

                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workExecution.executionLines ||
                        workExecution.executionLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No execution lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={executionLinesColumns}
                            dataSource={workExecution.executionLines}
                            scroll={{ x: 1400 }}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey="id"
                          />
                        )}
                      </div>

                      {workExecution.executionLines &&
                        workExecution.executionLines.length > 0 && (
                          <div className="mt-3 p-3 bg-light rounded">
                            <Row>
                              <Col lg="12">
                                <div className="row">
                                  <div className="col-md-3">
                                    <div className="d-flex justify-content-between">
                                      <span>Total Approved:</span>
                                      <strong>
                                        ₹{totalApprovedValue.toLocaleString()}
                                      </strong>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="d-flex justify-content-between">
                                      <span>Total Executed:</span>
                                      <strong className="text-success">
                                        ₹{totalExecutedValue.toLocaleString()}
                                      </strong>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="d-flex justify-content-between">
                                      <span>Remaining Value:</span>
                                      <strong className="text-warning">
                                        ₹{totalRemainingValue.toLocaleString()}
                                      </strong>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="d-flex justify-content-between">
                                      <span>Avg Progress:</span>
                                      <strong className="text-info">
                                        {avgProgress}%
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                    </div>
                  </Panel>

                  {/* Section C: Execution Batches */}
                  {workExecution.executionBatches &&
                    workExecution.executionBatches.length > 0 && (
                      <Panel
                        header={
                          <div className="d-flex align-items-center justify-content-between">
                            <>
                              📦 Execution Batches (
                              {workExecution.executionBatches?.length || 0})
                            </>
                          </div>
                        }
                        key="3"
                      >
                        <div className="mb-3">
                          <Tag color="blue">
                            {workExecution.executionBatches?.length || 0}{" "}
                            batch(es) submitted
                          </Tag>
                        </div>

                        <Timeline>
                          {workExecution.executionBatches
                            .sort(
                              (a, b) =>
                                new Date(b.submittedAt).getTime() -
                                new Date(a.submittedAt).getTime()
                            )
                            .map((batch) => (
                              <Timeline.Item
                                key={batch.id}
                                color={
                                  batch.status === "Approved"
                                    ? "green"
                                    : batch.status === "Rejected"
                                    ? "red"
                                    : batch.status === "Under Review"
                                    ? "blue"
                                    : "gray"
                                }
                              >
                                <div>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <strong>{batch.batchNumber}</strong>
                                      <Tag
                                        color={
                                          batch.status === "Approved"
                                            ? "green"
                                            : batch.status === "Rejected"
                                            ? "red"
                                            : batch.status === "Under Review"
                                            ? "blue"
                                            : "default"
                                        }
                                        className="ms-2"
                                      >
                                        {batch.status}
                                      </Tag>
                                      <div className="text-muted small mt-1">
                                        Period:{" "}
                                        {dayjs(
                                          batch.executionPeriodFrom
                                        ).format("DD/MM/YYYY")}{" "}
                                        -{" "}
                                        {dayjs(batch.executionPeriodTo).format(
                                          "DD/MM/YYYY"
                                        )}
                                      </div>
                                      <div className="text-muted small">
                                        Submitted by {batch.submittedBy} on{" "}
                                        {dayjs(batch.submittedAt).format(
                                          "DD/MM/YYYY HH:mm"
                                        )}
                                      </div>
                                      {batch.notes && (
                                        <div className="text-muted small mt-1">
                                          <strong>Notes:</strong> {batch.notes}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-end">
                                      <div className="fw-bold">
                                        ₹
                                        {batch.totalExecutedValue?.toLocaleString()}
                                      </div>
                                      <div className="text-success small">
                                        {batch.progressPercentage}% Progress
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Timeline.Item>
                            ))}
                        </Timeline>
                      </Panel>
                    )}
                </AntCollapse>
              </div>
            </div>
          </div>
        </Block>
      </Content>
    </>
  );
};

export default WorkExecutionView;
